<script setup lang="ts">
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import { adminProjectsService } from '@/services/admin/admin-projects-service.ts';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service.ts';
import { notificationService } from '@/services/notification/notification-service.ts';
import type { models } from 'cesg-shared';
import {
  PermissionType,
  ReviewPermissionType,
  UserRole,
} from 'cesg-shared/dist/models';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  projectId: string;
  user?: models.ProjectUsersResponse | null;
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const loading = ref(false);
const mode = computed(() => (props.user ? 'edit' : 'add'));

type PermissionOption = {
  value: string;
  text: string;
};

const reviewPermissionOptions: PermissionOption[] = [
  {
    value: ReviewPermissionType.ALLOWED,
    text: t('adminProjects.projectUserSettings.yes'),
  },
  {
    value: ReviewPermissionType.PROHIBITED,
    text: t('adminProjects.projectUserSettings.no'),
  },
];
const permissionOptions: PermissionOption[] = [
  {
    value: PermissionType.DISABLED,
    text: t('adminProjects.projectUserSettings.no'),
  },
  {
    value: PermissionType.VIEW,
    text: t('adminProjects.projectUserSettings.read'),
  },
  {
    value: PermissionType.COMMENT,
    text: t('adminProjects.projectUserSettings.comment'),
  },
  {
    value: PermissionType.EDIT,
    text: t('adminProjects.projectUserSettings.edit'),
  },
];

const selectedOverview = ref<PermissionType>(PermissionType.DISABLED);
const selectedBudget = ref<PermissionType>(PermissionType.DISABLED);
const selectedForecast = ref<PermissionType>(PermissionType.DISABLED);
const selectedAta = ref<PermissionType>(PermissionType.DISABLED);
const selectedInvoice = ref<PermissionType>(PermissionType.DISABLED);
const selectedFiles = ref<PermissionType>(PermissionType.DISABLED);
const selectedReview = ref<ReviewPermissionType>(
  ReviewPermissionType.PROHIBITED,
);

const permissionId = ref<string | undefined>(undefined);
const selectedUser = ref<models.ProjectUsersResponse | null | undefined>(
  undefined,
);
const selectedUserItem = ref<DropDownItem>({ text: '', value: '' });
const avaliableUsers = ref<models.ProjectUsersResponse[]>([]);
const avaliableUserItems = ref<DropDownItem[]>([]);
const userSettings = ref<models.UpdateUserSettingsBody>();

onMounted(() => {
  if (mode.value === 'edit') {
    selectedUser.value = props.user;
    setUserSettings(selectedUser.value);
    loadPermission();
  } else {
    loadAvaliableUsers();
  }
});

const loadPermission = async () => {
  if (!selectedUser.value) {
    return;
  }
  overlaySpinnerService.show();
  try {
    const permission = await adminProjectsService.getPermissionForUserInProject(
      props.projectId,
      selectedUser.value.id,
    );
    permissionId.value = permission.id ?? undefined;
    selectedOverview.value = permission.overview;
    selectedBudget.value = permission.budget;
    selectedForecast.value = permission.forecast;
    selectedAta.value = permission.ata;
    selectedInvoice.value = permission.invoice;
    selectedFiles.value = permission.files;
    selectedReview.value = permission.review;
  } catch (e) {
    console.error(e);
  } finally {
    overlaySpinnerService.hide();
  }
};

const loadAvaliableUsers = async () => {
  overlaySpinnerService.show();
  try {
    avaliableUsers.value =
      await adminProjectsService.getAvaliableUsersForProject(props.projectId);
    avaliableUserItems.value = avaliableUsers.value.map((user) => {
      return {
        text: `${user.email} - ${user.name}`,
        value: user.id,
      };
    });
  } catch (e) {
    console.error(e);
  } finally {
    overlaySpinnerService.hide();
  }
};

function selectUser() {
  selectedUser.value = avaliableUsers.value.find(
    (user) => user.id === selectedUserItem.value.value,
  );
  setUserSettings(selectedUser.value);
}

function setUserSettings(
  selectedUser: models.ProjectUsersResponse | null | undefined,
) {
  if (selectedUser) {
    userSettings.value = {
      name: selectedUser.name,
      phone: selectedUser.phone,
      role: selectedUser.role,
    };
  }
}

async function saveUserSettings() {
  if (!selectedUser.value || !userSettings.value) {
    return;
  }
  await adminProjectsService.updateUserSettings(
    selectedUser.value.id,
    userSettings.value,
  );
}

async function savePermission() {
  if (!selectedUser.value) {
    return;
  }
  const permission = {
    id: permissionId.value,
    projectId: props.projectId,
    userId: selectedUser.value.id,
    overview: selectedOverview.value,
    budget: selectedBudget.value,
    forecast: selectedForecast.value,
    ata: selectedAta.value,
    invoice: selectedInvoice.value,
    files: selectedFiles.value,
    review: selectedReview.value,
  };
  if (mode.value === 'edit') {
    await adminProjectsService.updatePermission(permission);
  } else {
    await adminProjectsService.addPermission(permission);
  }
}

async function save() {
  loading.value = true;
  try {
    await saveUserSettings();
    await savePermission();
    notificationService.open({
      text: t('adminProjects.projectUserSettings.settingsSavedNotification'),
      type: 'success',
    });
    emit('close');
  } catch {
    notificationService.open({
      text: t('adminProjects.projectUserSettings.settingsFailedNotification'),
      type: 'danger',
    });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="modal-overlay" @click.self="emit('close')">
    <div class="modal-box">
      <div class="modal-header">
        <h3 class="modal-title">
          {{
            mode === 'edit'
              ? t('adminProjects.projectUserSettings.title')
              : t('adminProjects.projectAddUser.title')
          }}
        </h3>
        <button class="close-button" @click="emit('close')">&times;</button>
      </div>

      <template v-if="selectedUser">
        <h4 class="title is-5">
          {{ t('adminProjects.projectUserSettings.settingsTitle') }}
        </h4>
        <table class="user-table">
          <thead>
            <tr>
              <th>{{ t('adminProjects.projectUserSettings.userName') }}</th>
              <th>{{ t('adminProjects.projectUserSettings.userEmail') }}</th>
              <th>
                {{ t('adminProjects.projectUserSettings.userPhoneNumber') }}
              </th>
              <th>
                {{ t('adminProjects.projectUserSettings.userRole') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="selectedUser && userSettings">
              <td>
                <input
                  v-model="userSettings.name"
                  :placeholder="t('adminProjects.projectUserSettings.userName')"
                />
              </td>
              <td>{{ selectedUser.email }}</td>
              <td>{{ selectedUser.phone }}</td>
              <td>
                <select v-model="userSettings.role">
                  <option disabled :value="UserRole.ADMIN">
                    {{ UserRole.ADMIN }}
                  </option>
                  <option :value="UserRole.CESG">
                    {{ UserRole.CESG }}
                  </option>
                  <option :value="UserRole.GUEST">
                    {{ UserRole.GUEST }}
                  </option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      <template v-else>
        <div class="select-user">
          <div class="select-user-dropdown">
            <drop-down
              v-model="selectedUserItem.value"
              :items="avaliableUserItems"
              :label="t('adminProjects.projectAddUser.avaliableUsers')"
            ></drop-down>
          </div>
          <button
            class="button"
            :disabled="!selectedUserItem.value"
            @click="selectUser"
          >
            {{ t('adminProjects.projectAddUser.addUser') }}
          </button>
        </div>
      </template>

      <hr class="strong-hr" />

      <h4 class="title is-5">
        {{ t('adminProjects.projectUserSettings.permissionTitle') }}
      </h4>
      <table class="project-table">
        <thead>
          <tr>
            <th>
              {{ t('adminProjects.projectUserSettings.projectTable.overview') }}
            </th>
            <th>
              {{
                t(
                  'adminProjects.projectUserSettings.projectTable.calculateBudget',
                )
              }}
            </th>
            <th>
              {{ t('adminProjects.projectUserSettings.projectTable.forecast') }}
            </th>
            <th>
              {{ t('adminProjects.projectUserSettings.projectTable.ataFs') }}
            </th>
            <th>
              {{ t('adminProjects.projectUserSettings.projectTable.invoices') }}
            </th>
            <th
              :title="
                t('adminProjects.projectUserSettings.projectTable.filesHint')
              "
            >
              {{ t('adminProjects.projectUserSettings.projectTable.files') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <select v-model="selectedOverview" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in permissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
            <td>
              <select v-model="selectedBudget" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in permissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
            <td>
              <select v-model="selectedForecast" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in permissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
            <td>
              <select v-model="selectedAta" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in permissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
            <td>
              <select v-model="selectedInvoice" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in permissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
            <td>
              <select v-model="selectedFiles" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in permissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>

      <table class="attest-table">
        <thead>
          <tr>
            <th>
              {{ t('adminProjects.projectUserSettings.reviewTable.review') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <select v-model="selectedReview" :disabled="!selectedUser">
                <option disabled value="">
                  {{ t('adminProjects.projectUserSettings.defaultStatus') }}
                </option>
                <option
                  v-for="o in reviewPermissionOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.text }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="modal-buttons">
        <button
          class="button save-user-button"
          :disabled="!selectedUser"
          @click="save"
        >
          {{ t('adminProjects.projectUserSettings.saveUser') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/style/popup';

h4 {
  margin-left: 4.5rem;
  padding-top: 0.5rem;
}

.select-user {
  margin-left: 0.5rem;
  padding-top: 1rem;
}

.select-user-dropdown {
  display: inline-flex;
  margin-left: 5%;
  margin-right: 0.5rem;
  width: 30rem;
}
</style>
